import React from "react";
import { Dialog, Grid2, Box, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  StyledDialogTitle,
  StyledDialogTitleInner,
  StyledDialogTitleText,
  StyledDialogTitleCloseButton,
  StyledDialogContent,
  StyledChargePatientBox,
  StyledChargePatientText,
  StyledChargePatientButton,
  StyledFollowUpBox,
  StyledFollowUpText,
  StyledFollowUpButton,
} from "./AppointmentDetailsModal.styles";
import {
  APPOINTMENT_DETAILS_TITLE,
  CHARGE_PATIENT_MESSAGE,
  CHARGE_PATIENT_BUTTON_TEXT,
  RESCHEDULE_BUTTON_TEXT,
  RESCHEDULE_MESSAGE,
  CANCEL_BUTTON_TEXT,
  CANCEL_MESSAGE,
} from "./AppointmentDetailsModal.constants";
import AppointmentDetailsModalStatusSection from "./components/AppointmentDetailsModalStatusSection";
import AppointmentDetailsModalClientInformationSection from "./components/AppointmentDetailsModalClientInformationSection";
import AppointmentDetailsModalScheduleAndLocationSection from "./components/AppointmentDetailsModalScheduleAndLocationSection";
import AppointmentDetailsModalFollowUpSection from "./components/AppointmentDetailsModalFollowUpSection";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EmailIcon from "@mui/icons-material/Email";
import CancelIcon from "@mui/icons-material/Cancel";
import { Gapper } from "../../../components/ui/Gapper";

const AppointmentDetailsModal = ({
  open,
  onClose,
  appointment,
  practitionerName,
  isPaid,
  isFreeConsultation,
  requiresPayment,
  onBookFollowUp,
  onChargePatient,
  onCancel,
  onReschedule,
  isPractitioner = false,
  consultationCompleted = true,
  agreementSigned = true,
  telehealthConsentSigned = true,
  isUpcomingSession = true,
}) => {
  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <StyledDialogTitleInner>
          <StyledDialogTitleText variant="h5" component="div">
            {APPOINTMENT_DETAILS_TITLE}
          </StyledDialogTitleText>
          <Box>
            <StyledDialogTitleCloseButton aria-label="close" onClick={onClose}>
              <CloseIcon onClick={onClose} />
            </StyledDialogTitleCloseButton>
          </Box>
        </StyledDialogTitleInner>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Gapper>
          <Gapper>
            <AppointmentDetailsModalStatusSection
              appointment={appointment}
              isUpcomingSession={isUpcomingSession}
              isPaid={isPaid}
              isFreeConsultation={isFreeConsultation}
              requiresPayment={requiresPayment}
            />
            <Divider />
          </Gapper>
          <Grid2 container spacing={2}>
            <AppointmentDetailsModalClientInformationSection
              appointment={appointment}
              practitionerName={practitionerName}
              isPractitioner={isPractitioner}
              agreementSigned={agreementSigned}
              telehealthConsentSigned={telehealthConsentSigned}
            />
            <AppointmentDetailsModalScheduleAndLocationSection
              appointment={appointment}
            />
            {isPractitioner && requiresPayment && onChargePatient && (
              <Grid2 item size={{ xs: 12 }}>
                <StyledChargePatientBox>
                  <StyledChargePatientText variant="body2">
                    {CHARGE_PATIENT_MESSAGE}
                  </StyledChargePatientText>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <StyledChargePatientButton
                      text={CHARGE_PATIENT_BUTTON_TEXT}
                      startIcon={<AttachMoneyIcon />}
                      onClick={() => {
                        onChargePatient(appointment);
                        onClose();
                      }}
                    />
                  </Box>
                </StyledChargePatientBox>
              </Grid2>
            )}
            {onBookFollowUp && (
              <AppointmentDetailsModalFollowUpSection
                appointment={appointment}
                onBookFollowUp={onBookFollowUp}
                onClose={onClose}
                isFreeConsultation={isFreeConsultation}
                isPractitioner={isPractitioner}
                consultationCompleted={consultationCompleted}
                agreementSigned={isPractitioner ? true : agreementSigned}
                telehealthConsentSigned={
                  isPractitioner ? true : telehealthConsentSigned
                }
              />
            )}
            {appointment.appointmentStatus?.toLowerCase() !== "cancelled" && (
                <Grid2 item size={{ xs: 12 }}>
                  <StyledFollowUpBox>
                    <StyledFollowUpText variant="body2">
                      {RESCHEDULE_MESSAGE}
                    </StyledFollowUpText>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <StyledFollowUpButton
                        text={RESCHEDULE_BUTTON_TEXT}
                        startIcon={<EmailIcon />}
                        onClick={() => {
                          window.location.href = "mailto:support@mynirva.com";
                        }}
                      />
                    </Box>
                  </StyledFollowUpBox>
                </Grid2>
              )}
            {onCancel && isUpcomingSession &&
              appointment.appointmentStatus?.toLowerCase() !== "cancelled" && (
                <Grid2 item size={{ xs: 12 }}>
                  <Box
                    sx={{
                      border: "1px solid rgba(244, 67, 54, 0.3)",
                      borderRadius: "14px",
                      backgroundColor: "rgba(244, 67, 54, 0.03)",
                      padding: 3,
                      textAlign: "center",
                    }}
                  >
                    <StyledFollowUpText
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {CANCEL_MESSAGE}
                    </StyledFollowUpText>
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <StyledFollowUpButton
                        text={CANCEL_BUTTON_TEXT}
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          onCancel(appointment);
                          onClose();
                        }}
                        sx={{
                          backgroundColor: "#F44336",
                          "&:hover": {
                            backgroundColor: "#D32F2F",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid2>
              )}
          </Grid2>
        </Gapper>
      </StyledDialogContent>
    </Dialog>
  );
};

export default AppointmentDetailsModal;
